/**
 * Memory 管理抽屉组件
 * 展示全部记忆，支持删除单条或全部，支持分页
 *
 * Note: user_id 不再需要从前端传递，后端会从 token 中获取当前用户邮箱
 */

import {
  Drawer,
  Text,
  Stack,
  Group,
  ActionIcon,
  Button,
  Modal,
  Box,
  Skeleton,
  ScrollArea,
  Pagination,
  Center,
} from '@mantine/core';
import { IconTrash, IconAlertTriangle, IconBrain, IconLoader2 } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { getMemories, deleteMemory, deleteAllMemories, MemoryItem } from '@mygpt/app/apis/services/memory';
import { useGPTStore } from '@/store';

interface MemoryManageDrawerProps {
  opened: boolean;
  onClose: () => void;
  botId: string;
}

const PAGE_SIZE = 10;

const formatDateTime = (dateStr?: string): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleString();
};

// 按时间倒序排序（最新的在前）
const sortMemoriesByTimeDesc = (memories: MemoryItem[]): MemoryItem[] => {
  return [...memories].sort((a, b) => {
    const timeA = new Date(a.updated_at || a.created_at || 0).getTime();
    const timeB = new Date(b.updated_at || b.created_at || 0).getTime();
    return timeB - timeA;
  });
};

export const MemoryManageDrawer = ({ opened, onClose, botId }: MemoryManageDrawerProps) => {
  const { t } = useTranslation();
  const { robotInfo } = useGPTStore();
  const queryClient = useQueryClient();
  const [deleteConfirmOpened, setDeleteConfirmOpened] = useState(false);
  const [deleteAllConfirmOpened, setDeleteAllConfirmOpened] = useState(false);
  const [memoryToDelete, setMemoryToDelete] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // 获取记忆列表（user_id 从后端 token 获取）
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['memories', botId],
    queryFn: () => getMemories(botId),
    enabled: opened,
    refetchOnWindowFocus: false,
  });

  // 删除单条记忆
  const deleteMutation = useMutation({
    mutationFn: (memoryId: string) => deleteMemory(memoryId, botId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memories', botId] });
      setDeleteConfirmOpened(false);
      setMemoryToDelete(null);
    },
  });

  // 删除所有记忆
  const deleteAllMutation = useMutation({
    mutationFn: () => deleteAllMemories(botId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memories', botId] });
      setDeleteAllConfirmOpened(false);
      setCurrentPage(1);
    },
  });

  // 按时间倒序排序
  const sortedMemories = useMemo(() => {
    const memories = data?.memories || [];
    return sortMemoriesByTimeDesc(memories);
  }, [data?.memories]);

  const totalCount = data?.total || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // 当前页的记忆
  const paginatedMemories = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return sortedMemories.slice(startIndex, startIndex + PAGE_SIZE);
  }, [sortedMemories, currentPage]);

  // 当数据变化时，确保当前页有效
  useMemo(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const handleDeleteClick = (memoryId: string) => {
    setMemoryToDelete(memoryId);
    setDeleteConfirmOpened(true);
  };

  const handleConfirmDelete = () => {
    if (memoryToDelete) {
      deleteMutation.mutate(memoryToDelete);
    }
  };

  const handleDeleteAllClick = () => {
    setDeleteAllConfirmOpened(true);
  };

  const handleConfirmDeleteAll = () => {
    deleteAllMutation.mutate();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <>
      <Drawer
        opened={opened}
        onClose={onClose}
        position="right"
        size="md"
        title={
          <Group gap="xs">
            <IconBrain size={20} />
            <Text fw={600}>{t('settings.memory.manage_title', 'Memory 管理')}</Text>
          </Group>
        }
      >
        <Stack gap="md">
          {/* Bot 名称 */}
          <Box className="bg-gray-50 rounded-lg p-3">
            <Text size="xs" c="gray.5">
              {t('settings.memory.bot_name', 'Bot')}
            </Text>
            <Text size="sm" fw={500}>
              {robotInfo?.name || '-'}
            </Text>
          </Box>

          {/* 操作栏 */}
          {sortedMemories.length > 0 && (
            <Group justify="space-between">
              <Text size="xs" c="gray.5">
                {t('settings.memory.total_count', { count: totalCount, defaultValue: `共 ${totalCount} 条记忆` })}
              </Text>
              <Button
                size="xs"
                variant="light"
                color="red"
                leftSection={<IconTrash size={14} />}
                onClick={handleDeleteAllClick}
              >
                {t('settings.memory.clear_all', '清除全部')}
              </Button>
            </Group>
          )}

          {/* 记忆列表 */}
          <ScrollArea h="calc(100vh - 300px)">
            {isLoading ? (
              <Stack gap="xs">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} height={80} radius="sm" />
                ))}
              </Stack>
            ) : error ? (
              <Text size="sm" c="red" ta="center">
                {t('settings.memory.load_error', '加载失败')}
              </Text>
            ) : sortedMemories.length === 0 ? (
              <Stack align="center" py="xl">
                <IconBrain size={48} color="gray" opacity={0.3} />
                <Text size="sm" c="gray.5">
                  {t('settings.memory.empty', '尚无记忆内容')}
                </Text>
                <Text size="xs" c="gray.4">
                  {t('settings.memory.empty_hint', '与 Bot 对话后，AI 会自动提取并记住重要信息')}
                </Text>
              </Stack>
            ) : (
              <Stack gap="xs">
                {isFetching && (
                  <Center pb="xs">
                    <IconLoader2 size={16} className="animate-spin" color="gray" />
                  </Center>
                )}
                {paginatedMemories.map((memory) => (
                  <Box key={memory.id} className="bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors">
                    <Group justify="space-between" align="flex-start">
                      <Stack gap={4} className="flex-1">
                        <Text size="sm" className="break-words">
                          {memory.content}
                        </Text>
                        <Text size="xs" c="gray.5">
                          {formatDateTime(memory.updated_at || memory.created_at)}
                        </Text>
                      </Stack>
                      <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => handleDeleteClick(memory.id)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  </Box>
                ))}
              </Stack>
            )}
          </ScrollArea>

          {/* 分页 */}
          {totalPages > 1 && (
            <Group justify="center">
              <Pagination total={totalPages} value={currentPage} onChange={handlePageChange} size="sm" />
            </Group>
          )}
        </Stack>
      </Drawer>

      {/* 删除单条记忆确认弹窗 */}
      <Modal
        opened={deleteConfirmOpened}
        onClose={() => {
          setDeleteConfirmOpened(false);
          setMemoryToDelete(null);
        }}
        title={
          <Group gap="xs">
            <IconAlertTriangle size={18} color="var(--mantine-color-red-6)" />
            <Text fw={600}>{t('settings.memory.delete_confirm_title', '删除记忆')}</Text>
          </Group>
        }
        centered
        size="sm"
      >
        <Stack>
          <Text size="sm">{t('settings.memory.delete_confirm_message', '确定删除这条记忆？删除后不可恢复。')}</Text>
          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={() => {
                setDeleteConfirmOpened(false);
                setMemoryToDelete(null);
              }}
            >
              {t('cancel', '取消')}
            </Button>
            <Button color="red" onClick={handleConfirmDelete} loading={deleteMutation.isPending}>
              {t('delete', '删除')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* 删除所有记忆确认弹窗 */}
      <Modal
        opened={deleteAllConfirmOpened}
        onClose={() => setDeleteAllConfirmOpened(false)}
        title={
          <Group gap="xs">
            <IconAlertTriangle size={18} color="var(--mantine-color-red-6)" />
            <Text fw={600}>{t('settings.memory.delete_all_confirm_title', '清除所有记忆')}</Text>
          </Group>
        }
        centered
        size="sm"
      >
        <Stack>
          <Text size="sm">
            {t('settings.memory.delete_all_confirm_message', '确定清除该 Bot 的所有记忆？此操作不可撤销。')}
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setDeleteAllConfirmOpened(false)}>
              {t('cancel', '取消')}
            </Button>
            <Button color="red" onClick={handleConfirmDeleteAll} loading={deleteAllMutation.isPending}>
              {t('settings.memory.clear_all', '清除全部')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
